import { IVersionedChoice, PropertySheet, PropertySheetCategory, TypedCardElementPeer } from "adaptivecards-designer";
import * as Adaptive from "adaptivecards";
import React from "react";
import Select from 'react-select';
import { createRoot } from 'react-dom/client';
import { Input } from "adaptivecards";
import { GetExpandedInputChoiceSet, handleValidationElement, initCustomRenderedElement, mapCardEnumProperties, mapPickerTreeNode, removeOtherPropertyConfigurations, SetTeamsExperiencePropertyConfigurations } from "../Utils/CustomElementUtils";
import { getPropertyEditorInstance, PresentationKeys, PropertyKeys } from "../Pickers/PickerBase";
import { colourStyles } from "../Typeahead/CollaboratorsTypeahead";
import { AdaptiveCardTexts } from "../../../shared/utils/constants";
import { ICustomProperty } from "../../../shared/interfaces/customproperties.interface";
import { CustomPropertyListPresentation_LABELS } from "../../../custom-properties/utils/constants";
import { AppStateValue } from "../../../AppState";
import { TicketProps } from "../../../shared/interfaces/ticketproperties.interface";
import _ from "lodash";
import { IDataSource } from "../DataSource/DataSourceTable";

const displayLabelProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "displayLabel", true);
const displayChoicesProperty = new Adaptive.EnumProperty(Adaptive.Versions.v1_0, "displayChoices", []);
const requireFieldProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "requireField");
const dataSourceProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "dataSource", true, undefined, "0");
const presentationProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "presentation", true);
const isMultiSelectProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "isMultiSelectField");
const isCustomPropertyProp = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "isCustomProperty", false);
const dataTypeProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "dataType", true);

export class ListProperty extends Adaptive.ChoiceSetInput {
    static readonly displayLabelProperty = displayLabelProperty;
    static readonly displayChoicesProperty = displayChoicesProperty;
    static readonly requireFieldProperty = requireFieldProperty;
    static readonly dataSourceProperty = dataSourceProperty;
    static readonly presentationProperty = presentationProperty;    
    static readonly isMultiSelectProperty = isMultiSelectProperty;    
    static readonly isCustomPropertyProp = isCustomPropertyProp;
    static readonly dataTypeProperty = dataTypeProperty;
    static onCustomValueChange: (input: Input) => void;
    static disableField: boolean;
    static presentInTeams: boolean;
    static JsonTypeName;
    static ElementId;
    static defaultLabel;
    static customPropertyData: ICustomProperty;
    static customPropertyDataSources: IDataSource[];
    static currentState: AppStateValue;

    constructor(paramType, paramCustomPropertyData: ICustomProperty) {
        ListProperty.JsonTypeName = paramType ?? ListProperty.JsonTypeName;
        ListProperty.ElementId = paramCustomPropertyData?.PropertyId ?? ListProperty.ElementId;
        ListProperty.defaultLabel = paramCustomPropertyData?.Name ?? ListProperty.defaultLabel;
        ListProperty.customPropertyData = paramCustomPropertyData ?? ListProperty.customPropertyData;
        
        super();
    }

    //#region Schema

    @Adaptive.property(displayLabelProperty)
    get displayLabel(): string | undefined {
        return this.getValue(ListProperty.displayLabelProperty);
    }

    set displayLabel(value: string | undefined) {
        this.setValue(ListProperty.displayLabelProperty, value);
        this.label = value;
        this.updateLayout();
    }

    @Adaptive.property(displayChoicesProperty)
    get displayChoices(): any[] | undefined {
        return this.getValue(ListProperty.displayChoicesProperty);
    }

    set displayChoices(value: any[] | undefined, ) {
        this.setValue(ListProperty.displayChoicesProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(requireFieldProperty)
    get requireField(): boolean | undefined {
        return this.getValue(ListProperty.requireFieldProperty);
    }

    set requireField(value: boolean | undefined) {
        this.setValue(ListProperty.requireFieldProperty, value);
        this.updateLayout();
    }
    
    @Adaptive.property(dataSourceProperty)
    get dataSource(): string | undefined {
        return this.getValue(ListProperty.dataSourceProperty);
    }

    set dataSource(value: string | undefined) {
        this.setValue(ListProperty.dataSourceProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(presentationProperty)
    get presentation(): string | undefined {
        return this.getValue(ListProperty.presentationProperty);
    }

    set presentation(value: string | undefined) {
        this.setValue(ListProperty.presentationProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(isMultiSelectProperty)
    get isMultiSelectField(): boolean | undefined {
        return this.getValue(ListProperty.isMultiSelectProperty);
    }

    set isMultiSelectField(value: boolean | undefined) {
        this.setValue(ListProperty.isMultiSelectProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(isCustomPropertyProp)
    get isCustomProperty(): boolean | undefined {
        return this.getValue(ListProperty.isCustomPropertyProp);
    }

    set isCustomProperty(value: boolean | undefined) {
        this.setValue(ListProperty.isCustomPropertyProp, value);
        this.updateLayout();
    }

    @Adaptive.property(dataTypeProperty)
    get dataType(): string | undefined {
        return this.getValue(ListProperty.dataTypeProperty);
    }

    set dataType(value: string | undefined) {
        this.setValue(ListProperty.dataTypeProperty, value);
        this.updateLayout();
    }

    //#endregion

    protected internalRender(): HTMLElement | undefined {
        
        if(!(this?.isCustomProperty ?? false))
            this.isCustomProperty = true;

        if((this?.id ?? "").length == 0)
            this.id = ListProperty.ElementId;

        if((this?.displayLabel ?? "").length == 0)
            this.displayLabel = ListProperty.defaultLabel;

        if((this?.dataType ?? "").length == 0)
            this.dataType = ListProperty.customPropertyData?.DataType ?? "";

        this.requireField ??= ListProperty.customPropertyData?.IsRequired ?? false;

        this.errorMessage ??= ListProperty.customPropertyData?.ErrorMessage ?? "";

        this.isMultiSelectField ??= ListProperty.customPropertyData?.CustomPropertyListConfiguration?.AllowMultiple ?? false;

        this.presentation ??= (CustomPropertyListPresentation_LABELS[ListProperty.customPropertyData?.CustomPropertyListConfiguration?.TeamsPresentation ?? ""] ?? "").toLowerCase();

        if(ListProperty.currentState){
            if(this.dataSource && parseInt(this.dataSource) > 0 && this.choices){
                this.displayChoices = this.choices;
            }else{
                const propertyData = ListProperty.currentState?.customProperties.find(p => p.PropertyId == this.id);
                let nestedList = _.cloneDeep(propertyData?.CustomPropertyListItems?.filter(pItem => !pItem.IsArchived && !pItem.IsDeleted) ?? []);
                const nestedChoices = mapPickerTreeNode((nestedList) as TicketProps[]);
                this.displayChoices = mapCardEnumProperties(nestedChoices);
            }
        }

        if((this?.presentation ?? "").length == 0)
            this.presentation = PresentationKeys.lookup;

        this.inputControlContainerElement.classList?.add("tikit-customProperty");

        if(this.presentation == PresentationKeys.lookup || !ListProperty.presentInTeams){

            let renderedElement = initCustomRenderedElement("tikit-input tikit-typeahead");

            let options = this.displayChoices ?? [];
            options = options.map(o => { return { "label": o.title, "value": o.value}; })

            let selectedValues = [];
            if(this.defaultValue){
                const valuesArr = this.defaultValue.split(",");
                selectedValues = options.filter(o => valuesArr.includes(o.value));
            }

            const domNode = document.createElement('div');
            const root = createRoot(domNode); 

            root.render(<Select placeholder={AdaptiveCardTexts.SEARCH_WORD} options={options} isClearable={false} isMulti={this.isMultiSelectField} menuPortalTarget={document.body} menuPosition="fixed" styles={colourStyles} defaultValue={selectedValues} {...ListProperty.disableField && {isDisabled: true}}
                onChange={(newValue) => {
                    if(!Array.isArray(newValue))
                        newValue = [newValue];
                    const values = newValue.map(x => x["value"]);
                    this.setValue(ListProperty.valueProperty, values.join());
                    handleValidationElement(renderedElement);
                    ListProperty?.onCustomValueChange(this);
                }}
            />);

            renderedElement.append(domNode);

            return renderedElement;

        }else
            return GetExpandedInputChoiceSet(this, this.isMultiSelectField);
    }

    get value(): string {
        return this.getValue(ListProperty.valueProperty);
    }

    getJsonTypeName(): string {
        return ListProperty.JsonTypeName;
    }

    updateLayout(processChildren: boolean = true) {
        super.updateLayout(processChildren);
    }

    internalValidateProperties(context: Adaptive.ValidationResults): void {
        //prevent choices from getting required
    }
}

export class ListPropertyPeer extends TypedCardElementPeer<ListProperty> {
    static displayLabelProperty = getPropertyEditorInstance(ListProperty.ElementId, PropertyKeys.displayLabel);
    static requireFieldProperty = getPropertyEditorInstance(ListProperty.ElementId, PropertyKeys.requireField);
    static dataSourceProperty = getPropertyEditorInstance(ListProperty.ElementId, PropertyKeys.dataSource, []);
    static presentationProperty = getPropertyEditorInstance(ListProperty.ElementId, PropertyKeys.presentation);
    static isMultiSelectProperty = getPropertyEditorInstance(ListProperty.ElementId, PropertyKeys.isMultiSelectField);
    static requiredMessageProperty = getPropertyEditorInstance(ListProperty.ElementId, PropertyKeys.errorMessage);
    

    static updateChoices(choices: IVersionedChoice[]) {
        this.dataSourceProperty = getPropertyEditorInstance(ListProperty.ElementId, PropertyKeys.dataSource, choices);
    }
        
    populatePropertySheet(propertySheet: PropertySheet, defaultCategory: string = PropertySheetCategory.DefaultCategory) {
        
        super.populatePropertySheet(propertySheet, defaultCategory);
        removeOtherPropertyConfigurations(propertySheet);
        
        propertySheet.add(
            defaultCategory,
            ListPropertyPeer.idProperty,
            ListPropertyPeer.displayLabelProperty,
            ListPropertyPeer.dataSourceProperty,
            ListPropertyPeer.isMultiSelectProperty
        );

        SetTeamsExperiencePropertyConfigurations(propertySheet, [ListPropertyPeer.presentationProperty]);

        propertySheet.add(
            "Validation",            
            ListPropertyPeer.requireFieldProperty,
            ListPropertyPeer.requiredMessageProperty
        );
    }
}
